import type { ContentTranslations } from '../i18n/content';
import type { Language } from '../i18n';
import api from './api';
import { BlogPost } from '../context/BlogContext';

// Interface to match the backend model
export interface BlogData {
  sourceLanguage?: Language;
  translations?: ContentTranslations;
  id?: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  image?: string;
  author: string;
  categories: string[];
  tags: string[];
  readTime: number;
  published: boolean;
  publishedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface BlogDraftRequest {
  topic: string;
  additionalDetails?: string;
  language: string;
  audience?: string;
  tone?: string;
}

export interface GeneratedBlogDraft {
  sourceLanguage: Language;
  translations: ContentTranslations;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image?: string;
  imagePrompt?: string;
  imageWarning?: string;
  categories: string[];
  tags: string[];
  readTime: number;
  sources?: Array<{
    title: string;
    url: string;
  }>;
}

// Function to map backend data to frontend format
const mapToFrontendBlog = (blog: BlogData): BlogPost => {
  return {
    id: blog.id?.toString() || '',
    contentLanguage: blog.sourceLanguage,
    sourceLanguage: blog.sourceLanguage,
    translations: blog.translations,
    title: blog.title,
    slug: blog.slug,
    date: blog.publishedAt ? new Date(blog.publishedAt).toISOString().split('T')[0] : 
          blog.createdAt ? new Date(blog.createdAt).toISOString().split('T')[0] : '',
    author: blog.author,
    excerpt: blog.excerpt,
    content: blog.content,
    image: blog.image || '',
    categories: blog.categories || [],
    tags: blog.tags || [],
    readTime: blog.readTime
  };
};

// Function to map frontend data to backend format
const mapToBackendBlog = (blog: Partial<BlogPost>): Partial<BlogData> => {
  return {
    sourceLanguage: blog.sourceLanguage,
    translations: blog.translations,
    title: blog.title,
    slug: blog.slug,
    content: blog.content,
    excerpt: blog.excerpt,
    image: blog.image,
    author: blog.author || 'Enzo Chevallier',
    categories: blog.categories || [],
    tags: blog.tags || [],
    readTime: blog.readTime || Math.ceil((blog.content?.split(' ').length || 0) / 200),
    published: true, // Default to published
  };
};

// Get all blogs
export const getAllBlogs = async (includeAdmin = false): Promise<BlogPost[]> => {
  try {
    const response = await api.get(includeAdmin ? '/blog/admin/all' : '/blog');
    return response.data.map(mapToFrontendBlog);
  } catch (error) {
    console.error('Error fetching blogs:', error);
    throw error;
  }
};

// Get a blog by slug
export const getBlogBySlug = async (slug: string): Promise<BlogPost> => {
  try {
    const response = await api.get(`/blog/${slug}`);
    return mapToFrontendBlog(response.data);
  } catch (error) {
    console.error(`Error fetching blog with slug ${slug}:`, error);
    throw error;
  }
};

// Create a new blog
export const createBlog = async (blog: Omit<BlogPost, 'id'>): Promise<BlogPost> => {
  try {
    const response = await api.post('/blog', mapToBackendBlog(blog));
    return mapToFrontendBlog(response.data);
  } catch (error) {
    console.error('Error creating blog:', error);
    throw error;
  }
};

// Update a blog
export const updateBlog = async (id: string, blog: Partial<BlogPost>): Promise<BlogPost> => {
  try {
    const response = await api.put(`/blog/${id}`, mapToBackendBlog(blog));
    return mapToFrontendBlog(response.data);
  } catch (error) {
    console.error(`Error updating blog with id ${id}:`, error);
    throw error;
  }
};

// Delete a blog
export const deleteBlog = async (id: string): Promise<void> => {
  try {
    await api.delete(`/blog/${id}`);
  } catch (error) {
    console.error(`Error deleting blog with id ${id}:`, error);
    throw error;
  }
};

export const generateBlogDraft = async (request: BlogDraftRequest): Promise<GeneratedBlogDraft> => {
  try {
    const response = await api.post('/ai/blog-draft', request, { timeout: 300000 });
    return response.data;
  } catch (error) {
    console.error('Error generating blog draft:', error);
    throw error;
  }
};
